import { memo, useCallback, useState } from 'react'
import { getCoordinates } from '../../../../api-client/identity'
import { APIErrors } from '../../../../api-client/enums'
import Loader from '../../../../components/loader'
import useDebouncedCallback from '../../../../hooks/use-debounced-callback'
import { FormField, Range } from '../../../../components/inputs'
import Checkbox from '../../../../components/checkbox'
import { defaultDistance, defaultLocation } from '../../config'
import { Button } from '../../../../components/button'

export default memo(function LocationFilter({
  location: initialLocation = defaultLocation,
  distance: initialDistance = defaultDistance,
  onLocationChange,
  onDistanceChange,
  onClose,
  name,
  onHeroClose,
}) {
  const [locationState, setLocation] = useState(initialLocation)
  const [distance, setDistance] = useState(initialDistance)
  const [error, setError] = useState(null)
  const [isFetching, setIsFetching] = useState(false)

  const shouldNoLimitBeChecked = (distance) => {
    return distance === undefined || isNaN(distance) || Number(distance) === Number(defaultDistance)
  }

  const [isNoLimitOnDistance, setIsNoLimitOnDistance] = useState({
    value: shouldNoLimitBeChecked(distance),
  })

  const fetchCoords = useCallback(
    async (zipCode) => {
      if (!zipCode?.trim()) {
        setError(null)
        return
      }

      try {
        setIsFetching(true)
        const res = await getCoordinates({ zipCode })

        setError(null)
        onLocationChange({
          ...locationState,
          zipCode,
          latitude: res.lat,
          longitude: res.lng,
        })
      } catch (error) {
        const errorCode = Number(error?.errors?.[0]?.code)
        let errorMessage = 'Something went wrong'

        if (
          [
            APIErrors.InvalidGoogleApiResponseStatusCode,
            APIErrors.InvalidZipCode,
            APIErrors.ZipCodeDoesNotExists,
            APIErrors.TimezoneNotFound,
            APIErrors.TimezoneNotSupported,
          ].includes(errorCode) ||
          errorCode === APIErrors.NotEmptyValidator
        ) {
          errorMessage = error?.errors?.[0]?.message || 'Postal code is invalid'
        }

        setError(errorMessage)
      } finally {
        setIsFetching(false)
      }
    },
    [locationState, onLocationChange],
  )

  const onZipCodeChangeDebounced = useDebouncedCallback((zipCode) => {
    fetchCoords(zipCode)
  }, 1000, [fetchCoords])

  const handleZipCodeChange = useCallback(
    (event) => {
      const value = event.target.value

      setLocation((prev) => ({ ...prev, zipCode: value }))
      setError(null)
      onZipCodeChangeDebounced(value)
    },
    [onZipCodeChangeDebounced],
  )

  const handleDistanceChangeDebounced = useDebouncedCallback(onDistanceChange, 500, [])

  const handleDistanceChange = useCallback(
    (distance) => {
      setDistance(distance)
      setIsNoLimitOnDistance({
        value: shouldNoLimitBeChecked(distance),
      })
      handleDistanceChangeDebounced(distance)
    },
    [handleDistanceChangeDebounced],
  )

  const handleCbxNoRadiusLimitCheck = (event) => {
    let newDistance = event.target.checked ? defaultDistance : defaultDistance - 1
    handleDistanceChange(newDistance)
  }

  return (
    <div className='locationFilter'>
      {isFetching && <Loader delay={400} fullPage />}

      <div className='zipCodeFromGroup'>
        <label htmlFor='locationFilterZipCode'>Postal Code</label>

        <FormField
          id='locationFilterZipCode'
          type='text'
          inputMode='text'
          value={locationState.zipCode || ''}
          onChange={handleZipCodeChange}
          error={!!error}
          data-hj-allow
        />
        {error && <p className='error'>{error}</p>}
      </div>

      <div className='distanceFormGroup'>
        <div className='noLimitContainer flex alignCenter'>
          <label htmlFor='cbxNoRadiusLimit'>No limit</label>
          <Checkbox
            checked={isNoLimitOnDistance.value}
            onClick={handleCbxNoRadiusLimitCheck}
            className='checkbox'
            id='cbxNoRadiusLimit'
          />
        </div>

        <label htmlFor='locationFilterSearchRadius'>Search Radius</label>

        <Range
          id='locationFilterSearchRadius'
          step={1}
          min={25}
          max={250}
          value={distance}
          onChange={handleDistanceChange}
        />
        <div className='distanceLabels flex justifyBetween'>
          <span>&lt;25miles</span>
          <span className='distanceValue'>{distance}miles</span>
          <span>250miles+</span>
        </div>
        <div className='apply-button'>
          <Button
            onClick={() => {
              onHeroClose ? onHeroClose() : onClose(name)
            }}
          >
            Apply
          </Button>
        </div>
      </div>

      <style jsx>{`
        .apply-button {
          display: flex;
          justify-content: center;
          margin: 3rem 0 0 0;
        }
        .locationFilter {
          max-width: 100vw;
          width: 43.6rem;
          min-height: 23.6rem;
          padding: 2rem;
          border-radius: 1rem;
          background: #fff;
          box-shadow: 0 0 0 1000vw rgba(0, 0, 0, 0.47);
        }

        .zipCodeFromGroup {
          margin: 0 0 2.4rem;
          padding: 0 0 2.4rem;
          border-bottom: 0.1rem solid var(--separator-line-color);
        }

        label {
          display: block;
          margin: 0 0 1.1rem;
          color: var(--secondary-text-color);
          font-size: 1.6rem;
          font-weight: 700;
        }

        .distanceLabels {
          margin: 0.5rem 0 0;
          color: var(--secondary-text-color);
          font-size: 1.2rem;
        }

        .error {
          margin: 0.5rem 0 0;
          color: var(--alert-color);
          text-align: center;
          font-size: 1.2rem;
          font-weight: 700;
        }

        .noLimitContainer {
          float: right;
          min-width: 9rem;

          :global(.checkbox) {
            margin: 0 0 0.9rem 0.5rem;
          }
        }

        @media (max-width: 1024px) {
          .locationFilter {
            box-shadow: none;
            padding: 3rem;
          }
        }
      `}</style>
    </div>
  )
})